/**
 * What the shell holds on behalf of every screen inside it (issue #301).
 *
 * Two things, and both exist because a screen must not go and get them for itself:
 *
 * - **`api`** — the request maker, carrying the origin and the *function* that reads a fresh
 *   session JWT per request. A screen building its own would be the place the "never cache the
 *   token" rule (ADR 0021 decision 3) gets forgotten once.
 * - **`reload`** — re-read the caller's memberships. This is how a screen answers a
 *   `family_deleting` or `not_a_member` refusal: an owner can disband a family while another
 *   member has the panel open, and the honest response is to ask what they are still in rather
 *   than to render a failure. The shell then shows the family as gone, switches to another, or
 *   hands over the server's own copy for that posture.
 *
 * It is a context rather than props because the screens are routed, and threading two values
 * through `<Routes>` is how one of them ends up re-derived.
 */

import { createContext, useContext } from "react";
import type { AppApi } from "./api/client";
import type { Membership } from "./api/memberships";

export interface Panel {
  readonly api: AppApi;
  /** The families this member can open, as the switcher offers them. */
  readonly families: readonly Membership[];
  /**
   * The signed-in account's address, for the header chip (#104).
   *
   * Held here rather than read from the SDK at each use, so the account named on every screen
   * is the one the shell decided somebody was signed in as. `null` only for a session whose
   * user carries no address at all.
   */
  readonly email: string | null;
  /** Re-read `/app/me/families`. Safe to call from a screen that was refused. */
  readonly reload: () => void;
  /**
   * End the browser's Stytch session (`@pilotfish/core`'s `endSession`, `forceClear`).
   *
   * Held here because two places offer it — the account menu in the header and the account
   * screen — and because the alternative is each of them reaching for the SDK client, which
   * is how one of them ends up without `forceClear` and strands somebody signed in.
   */
  readonly signOut: () => void;
}

const PanelContext = createContext<Panel | null>(null);

export const PanelProvider = PanelContext.Provider;

/**
 * The shell's panel state. Throws when used outside the shell, which is a programming error
 * rather than a state to render: the `<ErrorBoundary>` above catches it and shows the generic
 * card, and the console carries the real message.
 */
export function usePanel(): Panel {
  const panel = useContext(PanelContext);
  if (panel === null) throw new Error("usePanel() used outside the control panel shell");
  return panel;
}
